import { Chip } from '@/ui';
import type { AuthSession } from '@/auth/types';
import type { NavItem } from '@/components/sidebarTypes';
import type { NotificationContextValue } from '@/providers/notificationContext';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';
import type { SidebarItem, SidebarSection } from '@/ui/composites/SidebarNav';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';
import type { MouseEvent } from 'react';

type BuildSidebarContext = {
  session: AuthSession | null;
  navItems: NavItem[];
  activePath: string;
  onNavigate: (path: string) => void;
  featureFlags?: FeatureFlagResolveResult['flags'] | null;
  isAdmin: boolean;
  isSuperuser: boolean;
  planName: string | null;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  setSelectedOrganisationId: (value: string | null) => void;
  setSelectedProjectId: (value: string | null) => void;
  notify: NotificationContextValue['notify'];
  projectsOptions: Array<Pick<ProjectDto, 'id' | 'name' | 'slug'>>;
  allProjectsOptions: Array<Pick<ProjectDto, 'id' | 'name' | 'slug'>>;
  organisationsOptions: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject: boolean;
  lockProjectSelector: boolean;
  projectLabel: string;
  onAddProject: () => void;
  onAddOrganisation: () => void;
};

const isSelected = (
  itemPath: string,
  secondaryPath: string | undefined,
  selectedPath: string,
): boolean => {
  if (itemPath === '/') {
    return selectedPath === '/';
  }
  if (selectedPath.startsWith(itemPath)) {
    return true;
  }
  if (secondaryPath && selectedPath.startsWith(secondaryPath)) {
    return true;
  }
  return false;
};

const shouldHandleSidebarNavigationEvent = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  event.button === 0 &&
  !event.defaultPrevented &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

const buildSidebarItem = (
  item: NavItem,
  selectedPath: string,
  onNavigate: (path: string) => void,
): SidebarItem => ({
  id: item.key,
  label: item.label,
  href: item.path,
  icon: item.icon,
  active: isSelected(item.path, item.secondaryPath, selectedPath),
  onClick: (event: MouseEvent<HTMLAnchorElement>) => {
    if (!shouldHandleSidebarNavigationEvent(event)) return;
    event.preventDefault();
    onNavigate(item.path);
  },
});

const buildSecondaryItem = (
  item: NavItem,
  selectedPath: string,
  onNavigate: (path: string) => void,
): SidebarItem | null => {
  if (!item.secondary || !item.secondaryPath) return null;
  return {
    id: `${item.key}-secondary`,
    label: item.secondary,
    href: item.secondaryPath,
    active: isSelected(item.secondaryPath, undefined, selectedPath),
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      if (!shouldHandleSidebarNavigationEvent(event)) return;
      event.preventDefault();
      onNavigate(item.secondaryPath ?? item.path);
    },
  };
};

const pickNavItems = (
  items: NavItem[],
  keys: string[],
  selectedPath: string,
  onNavigate: (path: string) => void,
): SidebarItem[] => {
  const byKey = new Map(items.map((item) => [item.key, item]));
  return keys.flatMap((key) => {
    const match = byKey.get(key);
    if (!match) return [];
    const primaryItem = buildSidebarItem(match, selectedPath, onNavigate);
    const secondaryItem = buildSecondaryItem(match, selectedPath, onNavigate);
    return secondaryItem ? [primaryItem, secondaryItem] : [primaryItem];
  });
};

const buildWorkspaceSection = ({
  activePath,
  onNavigate,
  selectedOrganisationId,
  setSelectedOrganisationId,
  setSelectedProjectId,
  notify,
  projectsOptions,
  organisationsOptions,
  canCreateProject,
  lockProjectSelector,
  projectLabel,
  onAddProject,
  onAddOrganisation,
  featureFlags,
}: BuildSidebarContext): SidebarSection => {
  const organisationSelectorEnabled = featureFlags?.organisationSelector ?? false;
  const hasMultipleOrganisations = organisationsOptions.length > 1;
  const projectSelectorEnabled = featureFlags?.projectSelector ?? true;
  const organisationLabel = (() => {
    if (!organisationSelectorEnabled || !hasMultipleOrganisations) return 'Organisation';
    if (!selectedOrganisationId) return 'Select Organisation';
    const selectedOrganisation = organisationsOptions.find(
      (org) => org.id === selectedOrganisationId,
    );
    return selectedOrganisation?.name ?? 'Organisation';
  })();
  const organisationItems = organisationsOptions.map((org) => ({
    id: org.id,
    label: org.name,
    onClick: () => {
      setSelectedOrganisationId(org.id);
      setSelectedProjectId(null);
      notify({ message: `Switched to ${org.name}`, tone: 'blue' });
      onNavigate('/execution-runs');
    },
  }));
  const projectItems: Array<{ id: string; label: string; onClick?: () => void }> = [
    {
      id: 'all-projects',
      label: 'All Projects',
      onClick: () => {
        setSelectedProjectId(null);
        notify({ message: 'Viewing all projects', tone: 'blue' });
        onNavigate('/execution-runs');
      },
    },
    ...projectsOptions.map((project) => ({
      id: project.id,
      label: project.name,
      onClick: () => {
        setSelectedProjectId(project.id);
        notify({ message: `Switched to ${project.name}`, tone: 'blue' });
        onNavigate(activePath);
      },
    })),
  ];
  projectItems.push({
    id: 'add-project',
    label: 'Add New',
    ...(canCreateProject ? { onClick: onAddProject } : {}),
  });

  const items: SidebarItem[] = [
    ...(organisationSelectorEnabled && hasMultipleOrganisations
      ? [
          {
            id: 'workspace-organisation',
            label: organisationLabel,
            href: '#organisation',
            popover: {
              items: [
                ...organisationItems,
                {
                  id: 'add-organisation',
                  label: 'Add New Organisation',
                  onClick: onAddOrganisation,
                },
              ],
            },
          },
        ]
      : []),
    ...(projectSelectorEnabled
      ? [
          {
            id: 'workspace-project',
            label: projectLabel,
            href: '#projects',
            disabled: lockProjectSelector,
            disabledTone: 'default' as const,
            popover: lockProjectSelector
              ? undefined
              : {
                  title: 'Projects',
                  items: projectItems,
                },
          },
        ]
      : []),
  ];

  return { title: 'Workspace', items };
};

export const buildSidebarSections = (
  context: BuildSidebarContext,
): { sections: SidebarSection[]; footerItems: SidebarItem[] } => {
  const {
    session,
    navItems,
    activePath,
    onNavigate,
    featureFlags,
    isAdmin,
    isSuperuser,
    planName,
    selectedOrganisationId,
    selectedProjectId,
    organisationsOptions,
    projectsOptions,
    allProjectsOptions,
    lockProjectSelector,
  } = context;
  const groupedNavKeys = new Set([
    'dashboard',
    'reports',
    'releases',
    'features',
    'suites',
    'tests',
    'uploads',
    'pipelines',
  ]);
  const deliveryEnabled = featureFlags?.delivery ?? false;
  const operationsEnabled = featureFlags?.operations ?? false;
  const filteredNav = navItems.filter((item) => {
    if (item.requiresSuperuser) {
      return isSuperuser;
    }
    if (item.requiresAdmin) {
      return isAdmin;
    }
    return true;
  });
  const primaryUngrouped = filteredNav.filter(
    (item) => !groupedNavKeys.has(item.key) && item.key !== 'projects' && item.key !== 'superuser',
  );
  const fallbackOrganisationId = session?.lastOrganisationId ?? session?.orgId ?? null;
  const resolvedOrganisationId = selectedOrganisationId ?? fallbackOrganisationId;
  const selectedOrganisationSlug =
    resolvedOrganisationId === null
      ? null
      : (organisationsOptions.find((org) => org.id === resolvedOrganisationId)?.slug ?? null);
  const resolvedProjectId =
    selectedProjectId ??
    (lockProjectSelector && projectsOptions.length === 1 ? (projectsOptions[0]?.id ?? null) : null);
  const selectedProjectSlug =
    resolvedProjectId === null
      ? null
      : (projectsOptions.find((project) => project.id === resolvedProjectId)?.slug ??
        allProjectsOptions.find((project) => project.id === resolvedProjectId)?.slug ??
        null);
  const footerItems: SidebarItem[] = [
    ...(selectedOrganisationSlug
      ? [
          {
            id: 'organisation-settings',
            label: 'Organisation Settings',
            href: '/organisation-settings',
            active: isSelected('/organisation-settings', undefined, activePath),
            onClick: (event: MouseEvent<HTMLAnchorElement>) => {
              if (!shouldHandleSidebarNavigationEvent(event)) return;
              event.preventDefault();
              onNavigate('/organisation-settings');
            },
          },
        ]
      : []),
    ...(selectedProjectSlug
      ? [
          {
            id: 'project-settings',
            label: 'Project Settings',
            href: '/project-settings',
            active: isSelected('/project-settings', undefined, activePath),
            onClick: (event: MouseEvent<HTMLAnchorElement>) => {
              if (!shouldHandleSidebarNavigationEvent(event)) return;
              event.preventDefault();
              onNavigate('/project-settings');
            },
          },
        ]
      : []),
    ...(selectedOrganisationSlug
      ? [
          {
            id: 'plan-settings',
            label: 'Plan Settings',
            href: '/plan-settings',
            active: isSelected('/plan-settings', undefined, activePath),
            rightAdornment: <Chip label={planName ?? 'Loading'} size="xs" tone="default" />,
            onClick: (event: MouseEvent<HTMLAnchorElement>) => {
              if (!shouldHandleSidebarNavigationEvent(event)) return;
              event.preventDefault();
              onNavigate('/plan-settings');
            },
          },
        ]
      : []),
    ...(isSuperuser
      ? [
          {
            id: 'allowed-users',
            label: 'Superuser',
            href: '/superuser',
            active: isSelected('/superuser', undefined, activePath),
            onClick: (event: MouseEvent<HTMLAnchorElement>) => {
              if (!shouldHandleSidebarNavigationEvent(event)) return;
              event.preventDefault();
              onNavigate('/superuser');
            },
          },
        ]
      : []),
  ];
  const baseSections: SidebarSection[] = [
    buildWorkspaceSection(context),
    {
      title: 'Overview',
      items: pickNavItems(filteredNav, ['dashboard', 'reports'], activePath, onNavigate),
    },
    {
      title: 'Delivery',
      items: pickNavItems(filteredNav, ['releases', 'features'], activePath, onNavigate),
    },
    {
      title: 'Test',
      items: pickNavItems(filteredNav, ['uploads', 'suites', 'tests'], activePath, onNavigate),
    },
    {
      title: 'Operations',
      items: pickNavItems(filteredNav, ['pipelines'], activePath, onNavigate),
    },
    {
      title: 'Other',
      items: primaryUngrouped.map((item) => buildSidebarItem(item, activePath, onNavigate)),
    },
  ];
  const filteredSections = baseSections.filter((section) => {
    if (section.items.length === 0) return false;
    if (section.title === 'Delivery' && !deliveryEnabled) return false;
    if (section.title === 'Operations' && !operationsEnabled) return false;
    return true;
  });
  return { sections: filteredSections, footerItems };
};
